/**
 * trainingBackend.ts — トレーニングモード用のインメモリ偽バックエンド（2026-08-24）
 *
 * `/daylist/training` を開いている間、車両・予約の読み書きをここで完結させる。
 * dispatchClient.apiFetch がリクエストをここへ振り分けるので、
 * 画面側（DayListReservations / VehiclePositionMap / 同期サービス）は
 * 本番と同じコードのまま動く。DnD も保存も同期ポーリングもそのまま働き、
 * ただし通信は一切外に出ない。
 *
 * 【本物を使うもの】ステータスマスタ・スタッフ一覧（どちらも GET）。
 *   ラベルや配色・スタッフ名が本番と違うと練習にならないため。
 *   ダミーデータのステータス id も、この本物のマスタから解決する
 *   （店舗ごとに採番が違うので決め打ちしない）。
 *
 * 【データの寿命】タブを開いている間だけ。リロードで初期状態に戻る。
 */
import dayjs from 'dayjs';
import { apiFetchJson } from '../api/dispatchClient';

// ダミーの id 帯。本番データと絶対に衝突させない
const VEHICLE_ID_BASE = 9_100_000;
const RESERVATION_ID_BASE = 9_200_000;

interface StatusEntry { name: string; code: string; bg_color?: string | null; text_color?: string | null }

let seeded = false;
let seedPromise: Promise<void> | null = null;

let vehicles: any[] = [];
let reservations: any[] = [];

let vehicleStatusByCode: Record<string, { id: number; name: string }> = {};
let vehicleStatusById: Record<number, { code: string; name: string }> = {};
let resStatusByCode: Record<string, { id: number; name: string }> = {};
let resStatusById: Record<number, { code: string; name: string }> = {};
let staffById: Record<number, any> = {};

// ------------------------------------------------------------------
// マスタ読み込み（本物の GET）
// ------------------------------------------------------------------

const loadMasters = async (): Promise<void> => {
  try {
    const statuses = await apiFetchJson('statuses');
    const vehicleMap = (statuses?.data?.vehicle || {}) as Record<string, StatusEntry>;
    const resMap = (statuses?.data?.reservation || {}) as Record<string, StatusEntry>;
    vehicleStatusByCode = {};
    vehicleStatusById = {};
    Object.entries(vehicleMap).forEach(([id, s]) => {
      vehicleStatusByCode[s.code] = { id: Number(id), name: s.name };
      vehicleStatusById[Number(id)] = { code: s.code, name: s.name };
    });
    resStatusByCode = {};
    resStatusById = {};
    Object.entries(resMap).forEach(([id, s]) => {
      resStatusByCode[s.code] = { id: Number(id), name: s.name };
      resStatusById[Number(id)] = { code: s.code, name: s.name };
    });
  } catch (error) {
    console.error('[training] ステータスマスタの取得に失敗しました', error);
  }

  try {
    const payload = await apiFetchJson('staff?per_page=100');
    const list = Array.isArray(payload) ? payload : (payload?.data || []);
    staffById = {};
    list.forEach((s: any) => { if (s?.id != null) staffById[Number(s.id)] = s; });
  } catch (error) {
    console.error('[training] スタッフ一覧の取得に失敗しました', error);
  }
};

/** 車両ステータス code → id。店舗に無い code は fallback（既定は店舗）へ逃がす */
const vStatus = (code: string, fallback: string = 'airport_store') => {
  const hit = vehicleStatusByCode[code] || vehicleStatusByCode[fallback];
  return { id: hit?.id ?? 1, code: hit ? (vehicleStatusById[hit.id]?.code ?? code) : code, name: hit?.name ?? code };
};

const rStatus = (code: string) => {
  const hit = resStatusByCode[code];
  return { id: hit?.id ?? 1, code, name: hit?.name ?? code };
};

// ------------------------------------------------------------------
// ダミーデータ生成
// ------------------------------------------------------------------

const CHECK_DEFS = [
  { id: 1, name: '清掃', bg_color: '#10B981' },
  { id: 2, name: '洗車', bg_color: '#3B82F6' },
  { id: 3, name: '車内VC', bg_color: '#10B981' },
  { id: 4, name: '車外VC', bg_color: '#3B82F6' },
  { id: 5, name: 'ワイパー・ランプ類', bg_color: null },
];

/** 車両チェック（掃/洗/M）を n 段階でそれらしく散らす */
const buildChecks = (pattern: number) => CHECK_DEFS.map((c, i) => ({
  id: c.id,
  name: c.name,
  require_flg: true,
  value: ((pattern >> i) & 1) === 1,
  bg_color: c.bg_color ?? undefined,
}));

const CAR_TYPES = [
  { id: 9901, vehicle_type_name: '軽自動車', vehicle_type_code: 'K', bg_color: '#fde68a', text_color: '#000000' },
  { id: 9902, vehicle_type_name: 'コンパクト', vehicle_type_code: 'CP', bg_color: '#bfdbfe', text_color: '#000000' },
  { id: 9903, vehicle_type_name: 'ミニバン', vehicle_type_code: 'MV', bg_color: '#c7f9cc', text_color: '#000000' },
  { id: 9904, vehicle_type_name: 'ワゴン', vehicle_type_code: 'WG', bg_color: '#f5d0fe', text_color: '#000000' },
];

const emptyVehicleJson = () => ({
  data: { label: '', mark: '', bg_color: '', text_color: '' },
  types: { label: 'text', mark: 'text', bg_color: 'color', text_color: 'color' },
});

interface VehicleSpec {
  n: number;
  statusCode: string;
  type: number;
  checks: number;
  loc?: number | null;
  /** その店舗に statusCode が無い場合の代替（既定は店舗）。宮古には赤嶺駅・三井P等が無い */
  fallback?: string;
}

const VEHICLE_SPECS: VehicleSpec[] = [
  { n: 1, statusCode: 'airport_store', type: 0, checks: 0b11111 },
  { n: 2, statusCode: 'airport_store', type: 1, checks: 0b00011 },
  { n: 3, statusCode: 'airport_store', type: 2, checks: 0b00000 },
  { n: 4, statusCode: 'airport_store', type: 0, checks: 0b10011 },
  { n: 5, statusCode: 'yard', type: 1, checks: 0b00011 },
  { n: 6, statusCode: 'yard', type: 3, checks: 0b11011 },
  { n: 7, statusCode: 'departing', type: 2, checks: 0b00000 },
  { n: 8, statusCode: 'departing', type: 0, checks: 0b00000 },
  { n: 9, statusCode: 'departing', type: 1, checks: 0b00000 },
  { n: 10, statusCode: 'd_o', type: 3, checks: 0b00000, loc: 1 },
  { n: 11, statusCode: 'p_u', type: 0, checks: 0b11111, loc: 1 },
  { n: 12, statusCode: 'departing', type: 1, checks: 0b11111 },
  { n: 13, statusCode: 'mitsui_p', type: 2, checks: 0b00011 },
  { n: 14, statusCode: 'airport_store', type: 3, checks: 0b11111 },
  { n: 15, statusCode: 'airport_store', type: 0, checks: 0b00011 },
  { n: 16, statusCode: 'yard', type: 1, checks: 0b00000 },
  // 赤嶺駅ドロップオフ（宮古には無いので送り中へ逃がす）。
  // 「ドロップオフから出したら返却予約が貸出済みになる」動線の練習用
  { n: 17, statusCode: 'akamine_south_do', type: 2, checks: 0b00000, loc: 4, fallback: 'd_o' },
];

const buildVehicles = (): any[] => VEHICLE_SPECS.map(spec => {
  const st = vStatus(spec.statusCode, spec.fallback);
  const type = CAR_TYPES[spec.type];
  const tail = 9100 + spec.n;
  return {
    id: VEHICLE_ID_BASE + spec.n,
    vehicle_code: `TR-${type.vehicle_type_code}-W-${tail}`,
    vehicle_number: `沖縄 500 な ${tail}`,
    status: st.id,
    status_code: st.code,
    status_name: st.name,
    location_status: spec.loc ?? null,
    vehicle_type: type,
    bg_color: type.bg_color,
    text_color: type.text_color,
    reservation_box_priority: spec.n,
    out_of_use: false,
    active: 'ON',
    check_situations: buildChecks(spec.checks),
    json_data: emptyVehicleJson(),
  };
});

const vehicleId = (n: number) => VEHICLE_ID_BASE + n;

interface ResSpec {
  n: number;
  vehicle: number | null;
  /** 貸出(start)の時刻。返却専用なら null */
  start: string;
  end: string;
  /** 日付オフセット（0=本日, 1=明日） */
  startDay?: number;
  endDay?: number;
  name: string;
  statusCode: string;
  pax?: number;
  paxChildren?: number;
  childSeat?: number;
  boosterSeat?: number;
  juniorSeat?: number;
  arrivalFlight?: string;
  departureFlight?: string;
  contactPlan?: string;
  contactMedia?: string;
  shortMemo?: string;
  agreement?: boolean;
  license?: boolean;
  payment?: boolean;
  /** json_data.data に入れる値 */
  airport?: string;
  arrived?: boolean;
}

const RES_SPECS: ResSpec[] = [
  // --- 本日の貸出 ---
  {
    n: 1, vehicle: 12, start: '09:00', end: '18:00', endDay: 3, name: '山田 太郎',
    statusCode: 'in_use', pax: 2, agreement: true, license: true, payment: true,
    arrivalFlight: 'ANA 471', contactMedia: 'TEL',
  },
  {
    n: 2, vehicle: 11, start: '09:30', end: '17:00', endDay: 2, name: '佐藤 花子',
    statusCode: 'booked', pax: 3, paxChildren: 1, childSeat: 1,
    arrivalFlight: 'JTA 043', agreement: true, license: true, payment: false,
    airport: 'ロビー', arrived: true, contactMedia: 'LINE',
  },
  {
    n: 3, vehicle: 1, start: '10:00', end: '10:00', endDay: 4, name: '鈴木 一郎',
    statusCode: 'booked', pax: 4, paxChildren: 1, boosterSeat: 1,
    arrivalFlight: 'JAL 915', agreement: true, license: false, payment: false,
    contactMedia: 'MAIL',
  },
  {
    n: 4, vehicle: 2, start: '11:00', end: '11:00', endDay: 3, name: '高橋 美咲',
    statusCode: 'booked', pax: 2, agreement: true, license: true, payment: true,
    airport: '直接来店', arrived: true, contactMedia: 'LINE',
  },
  {
    n: 5, vehicle: 5, start: '14:00', end: '14:00', endDay: 5, name: '田中 健太',
    statusCode: 'booked', pax: 5, paxChildren: 2, childSeat: 1, juniorSeat: 1,
    contactPlan: 'セルフチェックイン', shortMemo: '追加ドライバー1名の確認が必要',
    agreement: false, license: false, payment: false, contactMedia: 'TEL',
  },
  {
    n: 6, vehicle: null, start: '13:00', end: '13:00', endDay: 4, name: '渡辺 翔',
    statusCode: 'booked', pax: 2, agreement: false, license: false, payment: false,
    arrivalFlight: 'SKY 593', contactMedia: 'MAIL',
  },
  {
    n: 7, vehicle: 14, start: '16:30', end: '16:30', endDay: 3, name: '中村 大輔',
    statusCode: 'request', pax: 6, paxChildren: 2, childSeat: 2,
    agreement: false, license: false, payment: false, contactMedia: 'PKG',
  },

  // --- 本日の返却（車両は利用中/ドロップオフ中）---
  {
    n: 8, vehicle: 7, start: '10:00', startDay: -3, end: '16:00', name: '小林 あゆみ',
    statusCode: 'in_use', pax: 2, agreement: true, license: true, payment: true,
    departureFlight: 'ANA 470',
  },
  {
    n: 9, vehicle: 8, start: '09:00', startDay: -2, end: '18:00', name: '加藤 隆',
    statusCode: 'in_use', pax: 4, agreement: true, license: true, payment: true,
    departureFlight: 'JTA 044',
  },
  {
    n: 10, vehicle: 9, start: '11:00', startDay: -4, end: '20:30', name: '吉田 誠',
    statusCode: 'in_use', pax: 3, agreement: true, license: true, payment: true,
  },
  {
    n: 11, vehicle: 10, start: '10:30', startDay: -2, end: '12:00', name: '松本 由美',
    statusCode: 'in_use', pax: 2, agreement: true, license: true, payment: true,
    departureFlight: 'SNA 2486',
  },

  {
    n: 14, vehicle: 17, start: '09:30', startDay: -3, end: '15:00', name: '森田 千夏',
    statusCode: 'in_use', pax: 2, agreement: true, license: true, payment: true,
  },

  // --- 明日の貸出（「明日貸出」グループの練習用）---
  {
    n: 12, vehicle: 3, start: '10:00', startDay: 1, end: '10:00', endDay: 4, name: '井上 拓也',
    statusCode: 'booked', pax: 2, agreement: false, license: false, payment: false,
  },
  {
    n: 13, vehicle: 15, start: '09:00', startDay: 1, end: '09:00', endDay: 3, name: '木村 彩',
    statusCode: 'booked', pax: 3, agreement: false, license: false, payment: false,
  },
];

const buildReservations = (): any[] => {
  const today = dayjs().startOf('day');
  return RES_SPECS.map(spec => {
    const st = rStatus(spec.statusCode);
    const start = today.add(spec.startDay ?? 0, 'day').format('YYYY-MM-DD') + ` ${spec.start}:00`;
    const end = today.add(spec.endDay ?? 0, 'day').format('YYYY-MM-DD') + ` ${spec.end}:00`;
    const jsonData: any = { data: {}, types: {} };
    if (spec.arrived) jsonData.data['到着連絡'] = '有り';
    if (spec.airport) jsonData.data['空港'] = spec.airport;
    return {
      id: RESERVATION_ID_BASE + spec.n,
      vehicle_id: spec.vehicle == null ? null : vehicleId(spec.vehicle),
      customer_name: spec.name,
      customer_tel: '090-0000-0000',
      customer_email: 'training@example.com',
      contact_code: `TR${String(RESERVATION_ID_BASE + spec.n).slice(-6)}`,
      start_date: start,
      end_date: end,
      status: st.id,
      status_code: st.code,
      status_name: st.name,
      short_memo: spec.shortMemo || '',
      pu_staff: null,
      do_staff: null,
      rent_staff: null,
      return_staff: null,
      bg_color: null,
      text_color: null,
      json_data: jsonData,
      detail: {
        pax: spec.pax ?? null,
        pax_children: spec.paxChildren ?? null,
        child_seat: spec.childSeat ?? null,
        booster_seat: spec.boosterSeat ?? null,
        junior_seat: spec.juniorSeat ?? null,
        arrival_flight: spec.arrivalFlight || '',
        departure_flight: spec.departureFlight || '',
        contact_plan: spec.contactPlan || null,
        contact_media: spec.contactMedia || '',
        agreement: spec.agreement ? 1 : 0,
        license: spec.license ? 1 : 0,
        payment: spec.payment ? 1 : 0,
        memo: '',
        communication_tools: [],
      },
    };
  });
};

// ------------------------------------------------------------------
// 公開 API（dispatchClient から呼ばれる）
// ------------------------------------------------------------------

export const ensureTrainingSeed = async (): Promise<void> => {
  if (seeded) return;
  if (!seedPromise) {
    seedPromise = (async () => {
      await loadMasters();
      vehicles = buildVehicles();
      reservations = buildReservations();
      seeded = true;
    })();
  }
  await seedPromise;
};

/** ダミーデータを初期状態に戻す（画面の「初期化」ボタン用） */
export const resetTrainingData = (): void => {
  vehicles = buildVehicles();
  reservations = buildReservations();
};

export const listTrainingVehicles = (): any[] => vehicles.map(v => ({ ...v }));

/**
 * 予約一覧。rental_date_* / return_date_* が来たら「貸出日 or 返却日が範囲内」で絞る
 * （本番APIと同じ OR セマンティクス。同期サービスは両方の範囲を同時に指定してくる）
 */
export const listTrainingReservations = (params: URLSearchParams): any[] => {
  const rentalFrom = params.get('rental_date_from');
  const rentalTo = params.get('rental_date_to');
  const returnFrom = params.get('return_date_from');
  const returnTo = params.get('return_date_to');
  const hasRental = !!(rentalFrom || rentalTo);
  const hasReturn = !!(returnFrom || returnTo);
  if (!hasRental && !hasReturn) return reservations.map(r => ({ ...r }));

  const inRange = (value: string, from: string | null, to: string | null) => {
    const d = String(value || '').substring(0, 10);
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  };

  return reservations
    .filter(r => (
      (hasRental && inRange(r.start_date, rentalFrom, rentalTo))
      || (hasReturn && inRange(r.end_date, returnFrom, returnTo))
    ))
    .map(r => ({ ...r }));
};

/** POST dispatch/vehicles/update 相当 */
export const trainingUpdateVehicle = (payload: any): any => {
  const idx = vehicles.findIndex(v => String(v.id) === String(payload?.id));
  if (idx < 0) throw new Error('トレーニングデータに該当の車両がありません');
  const current = vehicles[idx];
  const next = { ...current };

  Object.entries(payload || {}).forEach(([key, value]) => {
    if (key === 'id') return;
    next[key] = value;
  });

  // status を変えたら code/name も本物のマスタから引き直す
  if (payload?.status !== undefined) {
    const meta = vehicleStatusById[Number(payload.status)];
    if (meta) {
      next.status_code = meta.code;
      next.status_name = meta.name;
    }
  }

  next.updated_at = dayjs().toISOString();
  vehicles[idx] = next;
  return { ...next };
};

/** POST dispatch/reservations 相当（新規作成は練習対象外なので既存の更新だけ扱う） */
export const trainingSaveReservation = (payload: any): any => {
  const idx = reservations.findIndex(r => String(r.id) === String(payload?.id));
  if (idx < 0) throw new Error('トレーニングデータに該当の予約がありません');
  const current = reservations[idx];
  const next = { ...current };

  Object.entries(payload || {}).forEach(([key, value]) => {
    if (key === 'id' || key === 'detail' || key === 'json_data') return;
    if (key.endsWith('_staff_id')) return; // スタッフは下でオブジェクトに戻す
    if (value === undefined) return;
    next[key] = value;
  });

  if (payload?.detail && typeof payload.detail === 'object') {
    next.detail = { ...(current.detail || {}), ...payload.detail };
  }
  if (payload?.json_data && typeof payload.json_data === 'object') {
    next.json_data = payload.json_data;
  }

  // pu_staff_id 等 → スタッフオブジェクト（本物のスタッフ一覧から復元）
  (['pu_staff', 'do_staff', 'rent_staff', 'return_staff'] as const).forEach(field => {
    const key = `${field}_id`;
    if (!(key in (payload || {}))) return;
    const staffId = payload[key];
    next[field] = staffId == null ? null : (staffById[Number(staffId)] || { id: Number(staffId), staff_name: `ID:${staffId}` });
  });

  // status を変えたら code/name も引き直す
  if (payload?.status !== undefined) {
    const meta = resStatusById[Number(payload.status)];
    if (meta) {
      next.status = Number(payload.status);
      next.status_code = meta.code;
      next.status_name = meta.name;
    }
  }

  next.updated_at = dayjs().toISOString();
  reservations[idx] = next;
  return { ...next };
};

// ------------------------------------------------------------------
// リクエストルータ（dispatchClient.apiFetch から呼ばれる）
// ------------------------------------------------------------------

const jsonResponse = (body: any): Response => new Response(JSON.stringify(body), {
  status: 200,
  headers: { 'Content-Type': 'application/json' },
});

const parseBody = (init: any): any => {
  const body = init?.body;
  if (!body) return {};
  if (typeof body === 'string') {
    try { return JSON.parse(body); } catch { return {}; }
  }
  return body;
};

/**
 * トレーニングモード中のリクエストを捌く。
 *
 * - 車両・予約の GET/POST … ダミーデータで応答（本番には出さない）
 * - それ以外の GET        … null を返して本物へ素通し（マスタ・スタッフ・LINE等）
 * - それ以外の 非GET      … 例外。本番バックエンドへの書き込みを絶対に通さないための最終防波堤
 */
export const handleTrainingRequest = async (path: string, init: any): Promise<Response | null> => {
  const method = String(init?.method || 'GET').toUpperCase();
  const [rawPath, queryString = ''] = String(path).split('?');
  const clean = rawPath.replace(/^\/+/, '').replace(/\/+$/, '');
  const params = new URLSearchParams(queryString);

  if (method === 'GET') {
    if (clean === 'dispatch/vehicles') {
      await ensureTrainingSeed();
      return jsonResponse(listTrainingVehicles());
    }
    if (clean === 'dispatch/reservations') {
      await ensureTrainingSeed();
      return jsonResponse(listTrainingReservations(params));
    }
    return null; // 読み取りは素通し（本物のマスタを使う）
  }

  await ensureTrainingSeed();
  if (clean === 'dispatch/vehicles/update') {
    return jsonResponse(trainingUpdateVehicle(parseBody(init)));
  }
  if (clean === 'dispatch/reservations') {
    return jsonResponse(trainingSaveReservation(parseBody(init)));
  }

  throw new Error(`トレーニングモード中はこの操作を保存できません (${method} ${clean})`);
};
